// Evidence Record validator. E-levels are assigned here — never from task titles.
// Structured fields are primary for E4/E5; text keywords support, not determine, truth.

import { ACTIVITY, EVIDENCE_BY_ID, EVIDENCE_LEVELS } from './constants';

const LEVEL_IDS = new Set([ACTIVITY.id, ...EVIDENCE_LEVELS.map((e) => e.id)]);

const PROOF_RE = {
  E5: /\b(paid|payment received|invoice settled|signed|countersigned|executed mandate|binding contract|mandate awarded)\b/i,
  E4: /\b(budget|proposal requested|asked for a proposal|priced|poc approved|mandate negotiation|architecture accepted|formal next gate|term sheet)\b/i,
  E3: /\b(documents|data room|asset identified|named asset|second meeting|technical team|legal team|workflow data|requirements shared|shared data|due diligence|title information)\b/i,
  E2: /\b(confirmed (the )?problem|confirmed pain|use case confirmed|acknowledged the issue|validated the problem)\b/i,
  E1: /\b(replied|reply|intro|introduction|meeting booked|call booked|scheduled a call|responded)\b/i,
  ACT: /\b(researched|sent|emailed|prepared|built|designed|posted|deck|list|drafted|updated the site|social media)\b/i,
};

const INTERNAL_MOTION_RE =
  /\b(build (the )?(list|universe|site|deck|crm)|research(ed)?|internal|import(ed)?|verify(ing)? the first|scope cleanup|social media|virialai)\b/i;

const OUT_OF_SCOPE_RE = /\b(virialai|viral ai|social media presence)\b/i;

const NAMED_ASSET_RE = /\b(named asset|data room|documents shared|title information|asset identified)\b/i;

const E4_COMMITMENT_TYPES = new Set([
  'proposal_requested',
  'budget_confirmed',
  'poc_approved',
  'mandate_negotiation',
  'architecture_accepted',
  'formal_next_gate',
  'term_sheet',
  'commercial_commitment',
  'execution_commitment',
]);

const E5_ARTIFACT_TYPES = new Set([
  'signed_agreement',
  'payment',
  'mandate_awarded',
  'binding_contract',
  'executable_commitment',
  'invoice_settled',
]);

export function classifyEvidenceText(text) {
  const t = String(text || '');
  for (const [level, re] of Object.entries(PROOF_RE)) {
    if (re.test(t)) return level;
  }
  return null;
}

function hasCounterparty(record) {
  return !!String(record.counterparty || '').trim();
}

function hasDatedSource(record) {
  return !!(String(record.date || '').trim() || String(record.source || '').trim() || String(record.eventDate || '').trim());
}

function hasSupportingFact(record) {
  return !!(String(record.description || '').trim() || String(record.title || '').trim() || String(record.supportingFact || '').trim());
}

function structuredE4Ok(record) {
  const type = String(record.commitmentType || record.proofType || '').trim();
  if (type && E4_COMMITMENT_TYPES.has(type)) {
    return hasCounterparty(record) && hasDatedSource(record) && hasSupportingFact(record);
  }
  return false;
}

function structuredE5Ok(record) {
  const type = String(record.artifactType || record.proofType || record.commitmentType || '').trim();
  if (type && E5_ARTIFACT_TYPES.has(type)) {
    return hasCounterparty(record) && hasSupportingFact(record);
  }
  return false;
}

/**
 * Assign an evidence level from an Evidence Record only.
 * Task titles cannot promote E4/E5. Internal motion cannot claim proof.
 * E2 + named-asset facts normalize to E3 (stronger), never "demoted".
 */
export function validateEvidenceLevel(record = {}, opts = {}) {
  const { fromActionTitle = false } = opts;
  const title = String(record.title || '');
  const description = String(record.description || '');
  const blob = `${title} ${description} ${record.counterparty || ''}`;
  const requested = String(record.level || '').trim();
  const suggested = classifyEvidenceText(blob);

  if (fromActionTitle) {
    return {
      ok: false,
      level: 'ACT',
      reason: 'Evidence level is assigned only on Evidence Records, never from a task title.',
      suggested: suggested || 'ACT',
    };
  }

  // Internal motion cannot claim E4/E5 even with a named counterparty filler.
  if (INTERNAL_MOTION_RE.test(blob) && (requested === 'E4' || requested === 'E5' || suggested === 'E4' || suggested === 'E5')) {
    if (!structuredE4Ok(record) && !structuredE5Ok(record)) {
      return {
        ok: false,
        level: 'ACT',
        reason: 'Internal motion cannot be promoted to E4 or E5. Record Activity, or name a structured counterparty commitment.',
        suggested: 'ACT',
      };
    }
  }

  // E2 selected but facts describe named asset / documents → normalize to E3.
  if ((requested === 'E2' || suggested === 'E2') && NAMED_ASSET_RE.test(blob)) {
    return {
      ok: false,
      level: 'E3',
      reason: 'Recorded facts support E3 (named asset / documents / information commitment), not E2. Corrected to E3.',
      suggested: 'E3',
      normalized: true,
    };
  }

  let level = requested || suggested || 'E0';
  if (!LEVEL_IDS.has(level)) level = 'E0';

  if (level === 'E4') {
    if (!hasCounterparty(record)) {
      return {
        ok: false,
        level: 'ACT',
        reason: 'Proof without a named counterparty is an assertion.',
        suggested: 'ACT',
      };
    }
    if (structuredE4Ok(record)) {
      return { ok: true, level: 'E4', reason: '', suggested: 'E4' };
    }
    if (!hasDatedSource(record) || !hasSupportingFact(record)) {
      return {
        ok: false,
        level: suggested && weight(suggested) < 4 ? suggested : 'E3',
        reason: 'E4 requires commitment type (or clear commercial facts), named counterparty, dated event/source, and supporting fact.',
        suggested: suggested && weight(suggested) < 4 ? suggested : 'E3',
      };
    }
    // Keywords may support when structure is partial but counterparty + dated + fact exist and text matches.
    if (!(suggested === 'E4' || PROOF_RE.E4.test(blob))) {
      return {
        ok: false,
        level: suggested && weight(suggested) < 4 ? suggested : 'E3',
        reason: 'E4 claim lacks a commercial/execution commitment type or matching facts.',
        suggested: suggested && weight(suggested) < 4 ? suggested : 'E3',
      };
    }
  }

  if (level === 'E5') {
    if (!hasCounterparty(record)) {
      return {
        ok: false,
        level: 'ACT',
        reason: 'Proof without a named counterparty is an assertion.',
        suggested: 'ACT',
      };
    }
    if (structuredE5Ok(record)) {
      return { ok: true, level: 'E5', reason: '', suggested: 'E5' };
    }
    if (!hasSupportingFact(record)) {
      return {
        ok: false,
        level: suggested && weight(suggested) < 5 ? suggested : 'E3',
        reason: 'E5 requires a binding artifact/event type, named counterparty, and supporting fact.',
        suggested: suggested && weight(suggested) < 5 ? suggested : 'E3',
      };
    }
    if (!(suggested === 'E5' || PROOF_RE.E5.test(blob))) {
      return {
        ok: false,
        level: suggested && weight(suggested) < 5 ? suggested : 'E3',
        reason: 'E5 claim lacks a binding artifact (signed agreement, payment, mandate, etc.).',
        suggested: suggested && weight(suggested) < 5 ? suggested : 'E3',
      };
    }
  }

  if ((level === 'E4' || level === 'E5') && !hasCounterparty(record)) {
    return {
      ok: false,
      level: suggested && suggested !== 'E4' && suggested !== 'E5' ? suggested : 'ACT',
      reason: 'Proof without a named counterparty is an assertion.',
      suggested: 'ACT',
    };
  }

  if (requested && suggested && weight(requested) > weight(suggested) && weight(requested) >= 4) {
    return {
      ok: false,
      level: suggested,
      reason: `Recorded facts support ${suggested}, not ${requested}. Do not promote the label.`,
      suggested,
    };
  }

  // Opaque E4/E5 with counterparty but no proof structure and no keywords.
  if ((level === 'E4' || level === 'E5') && !suggested && !structuredE4Ok(record) && !structuredE5Ok(record)) {
    return {
      ok: false,
      level: 'E3',
      reason: `${level} requires structured commitment/artifact fields or clear supporting commercial facts.`,
      suggested: 'E3',
    };
  }

  return { ok: true, level, reason: '', suggested: suggested || level };
}

function weight(id) {
  if (id === 'OPS' || id === 'ACT') return -1;
  return EVIDENCE_BY_ID[id]?.weight ?? -1;
}

export function actionEvidenceCredit(action) {
  const expected = action?.expectedEvidence;
  if (!expected || expected === 'OPS') {
    return { credit: false, label: 'Internal enabler—no evidence credit' };
  }
  return { credit: true, label: expected, target: expected };
}

export function isOutOfScopeAction(action, exceptions = []) {
  const blob = `${action?.text || ''} ${action?.workstream || ''} ${action?.linkedLabel || ''}`.toLowerCase();
  if (!OUT_OF_SCOPE_RE.test(blob)) return false;
  const approved = (exceptions || []).some((e) => {
    const decided = String(e.decision || '').toLowerCase();
    // Canonical: Approved. Legacy: Approve / approved / accepted.
    if (!['approved', 'approve', 'accepted'].includes(decided)) return false;
    return OUT_OF_SCOPE_RE.test(JSON.stringify(e).toLowerCase());
  });
  return !approved;
}

export { LEVEL_IDS };
